import React, { useState } from 'react';
import { MessageSquareIcon } from 'lucide-react';
type Space = 'Sales' | 'Engineering' | 'Marketing';
interface Recap {
  title: string;
  id: string;
  time: string;
  description: string;
  space: Space;
}
const SPACES: {
  name: Space;
  label: string;
}[] = [{
  name: 'Sales',
  label: 'S'
}, {
  name: 'Engineering',
  label: 'E'
}, {
  name: 'Marketing',
  label: 'MA'
}];
const SPACE_DOT_COLORS: Record<Space, string> = {
  Sales: 'bg-blue-500',
  Engineering: 'bg-emerald-500',
  Marketing: 'bg-purple-500'
};
const RECAPS: Recap[] = [{
  title: 'Positioning: breadth vs depth, not power vs flexibility',
  id: '371d2e',
  time: '08:14',
  description: 'Tony worried about Operator being in a dead zone between out-of-the-box consumer tools and specialized enterprise verticals.',
  space: 'Sales'
}, {
  title: 'Refined ICP: team-level buyers, not just company-level',
  id: '371d2e',
  time: '08:20',
  description: 'Tony described friend managing sales team at a SaaS company who has a business engineer wiring things for him.',
  space: 'Sales'
}, {
  title: 'Middle-zone analogies and GTM motion analysis',
  id: '371d2e',
  time: '08:29',
  description: 'Tony asked for analogies of products that won in middle-zone and ones that got stuck.',
  space: 'Sales'
}, {
  title: 'Sharper ICP: RevOps/MarOps at mid-market companies',
  id: '371d2e',
  time: '11:33',
  description: 'Tony reframed the goal: building a 9-figure business not winning a category.',
  space: 'Sales'
}, {
  title: 'Alex heartbeat — no changes',
  id: 'ea474f',
  time: '12:20',
  description: '40th consecutive no-change sync. AgentMail thread counts unchanged.',
  space: 'Engineering'
}, {
  title: 'Cron-to-channel Slack mappings',
  id: 'b544d5',
  time: '14:32',
  description: 'Added cron-level notification channel routing.',
  space: 'Engineering'
}, {
  title: 'Operator README audit for new-dev onboarding',
  id: '8a916f',
  time: '15:34',
  description: 'Audited README — last touched 2026-03-04, ~7 weeks of commits since.',
  space: 'Engineering'
}, {
  title: '41st no-change heartbeat',
  id: '3520e6',
  time: '17:04',
  description: 'Alex heartbeat at 15:34 UTC: No material changes.',
  space: 'Engineering'
}, {
  title: 'README plan refined: ASCII permissions diagrams',
  id: '8a916f',
  time: '17:10',
  description: 'Tony wants ASCII diagrams explaining permissions.',
  space: 'Engineering'
}, {
  title: 'Rewrote operator-app README',
  id: '8a916f',
  time: '17:11',
  description: 'Rewrote README to restructure around concepts-first for new-dev onboarding.',
  space: 'Engineering'
}, {
  title: 'Landing page v3 structure — single-player + multiplayer layers',
  id: '371d2e',
  time: '17:25',
  description: 'Tony starting new landing page with hero: A simple but powerful control plane.',
  space: 'Marketing'
}, {
  title: 'Built landing-4 with scratching the surface hero',
  id: '371d2e',
  time: '17:25',
  description: 'Created landing-4.erb and added /landing-4 route.',
  space: 'Marketing'
}];
export function RecapsFeed() {
  const [activeSpace, setActiveSpace] = useState<Space | null>(null);
  const filtered = activeSpace ? RECAPS.filter(r => r.space === activeSpace) : RECAPS;
  return <div className="w-full max-w-[800px] mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-neutral-200 flex flex-col h-[65vh] max-h-[600px]" data-id="element-5416" data-mp="3eiimnk64fjpk">
      <div className="px-5 py-4 border-b border-neutral-100 shrink-0 bg-neutral-50/50 flex items-start justify-between" data-id="element-5417" data-mp="3ncx15kituq7m">
        <div data-id="element-5418" data-mp="3sagr3how0rfe">
          <h2 className="text-lg font-bold text-[#2B211A]" data-id="element-5419" data-mp="3s9x4vos7fgvk">Recaps</h2>
          <p className="text-xs text-neutral-500" data-id="element-5420" data-mp="14xgkhymay7h">
            Today's activity across your organization
          </p>
        </div>
        <div className="flex items-center gap-1.5 pt-0.5" data-id="element-5421" data-mp="63rxfed0ycoy">
          {SPACES.map(space => <button key={space.name} onClick={() => setActiveSpace(activeSpace === space.name ? null : space.name)} className={`relative w-6 h-6 rounded-md text-[10px] font-bold flex items-center justify-center transition-all group ${activeSpace === space.name ? 'bg-neutral-900 text-white' : activeSpace === null ? 'bg-neutral-300 text-neutral-700 hover:bg-neutral-400' : 'bg-neutral-200 text-neutral-400 hover:bg-neutral-300 hover:text-neutral-600'}`} title={space.name} data-id="element-5422" data-mp="22xlj0p3fthm7">
              {space.label}
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2 py-1 bg-neutral-900 text-white text-[10px] font-medium rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg z-10" data-id="element-5423" data-mp="6a9cehqx7gzu">
                {space.name}
              </span>
            </button>)}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-id="element-5424" data-mp="3jnugxfirzuft">
        <div className="flex flex-col" data-id="element-5425" data-mp="3jn2q0i04ikvv">
          {filtered.map((recap, idx) => <div key={idx} className="flex items-start gap-3 px-5 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors" data-id="element-5426" data-mp="2vxi2rmwxt1gs" data-mpi={idx}>
              <div className="pt-1 shrink-0" data-id="element-5427" data-mp="2qyx0zj21e9q8">
                <div className={`w-2 h-2 rounded-full ${SPACE_DOT_COLORS[recap.space]}`} data-id="element-5428" data-mp="16hec8xyekbd8" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-5429" data-mp="1b84kciy7qsbm">
                <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-5430" data-mp="1b95zozm9i0z5">
                  <h3 className="text-sm font-semibold text-[#2B211A] truncate" data-id="element-5431" data-mp="xi13k7i3rqtk">
                    {recap.title}
                  </h3>
                  <div className="flex items-center gap-2 shrink-0" data-id="element-5432" data-mp="1q55eet3vmzd9">
                    <button className="text-neutral-400 hover:text-neutral-600 transition-colors" title="Open thread" data-id="element-5433" data-mp="1q5wxj4gaf4mn">
                      <MessageSquareIcon className="w-3.5 h-3.5" data-id="element-5434" data-mp="1u4e85xyx2x8t" />
                    </button>
                    <span className="text-xs text-neutral-400 font-medium w-10 text-right" data-id="element-5435" data-mp="1g924h12s9wfr">
                      {recap.time}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-neutral-500 line-clamp-1" data-id="element-5436" data-mp="4tvfpbvr5lpi">
                  {recap.description}
                </p>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}